import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SendIcon from '@/assets/all-icons/send-icon';
import config from '@/config';
import ArtworkDataContext from '@/context/ArtworkDataContext';
import useTranslations from '@/i18n/useTranslations';
import fetchWrapper from '@/services/fetchWrapper.service';
import { getPreferredLocale } from '@/services/locale.service';
import AskAiQuotaNotice from '@/shared-components/ai/components/AskAiQuotaNotice';
import useAskAiEntitlement from '@/shared-components/ai/useAskAiEntitlement';
import SuartePlusPopup from '@/shared-components/popups/components/SuartePlusPopup';
import Text from '@/shared-components/text/components/Text';
import { resolveAiAccessErrorMessage } from '@/utils/aiAccessErrors';
import { getArtworkArtistDisplayName } from '@/utils/artworkArtists';

import SocialsHeader from '../components/SocialsHeader';

import AiRecommendArtworkCard from './AiRecommendArtworkCard';

import './index.css';
import '@/shared-components/ai/styles/AiConversationComposer.css';

const MAX_QUESTION_LENGTH = 400;
// Reveal pacing controls:
// - Lower interval => faster updates.
// - Higher chars per tick => faster full reveal.
// Tune these 3 values together to control typing speed.
const REVEAL_INTERVAL_MS = 24;
const REVEAL_MIN_CHARS = 2;
const REVEAL_MAX_CHARS = 9;
const EMERGENCY_WELCOME_MESSAGE = 'Ask a question.';
const normalizeText = (value) => (typeof value === 'string' ? value.trim() : '');
const interpolateTemplate = (template, values = {}) =>
  normalizeText(template).replace(/\{(\w+)\}/g, (match, key) => values[key] ?? match);
const formatMessageTime = (timestamp, locale) => {
  if (!timestamp) return null;
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
};

export default function ArtworkAi({ closeSocial }) {
  const navigate = useNavigate();
  const t = useTranslations('common.profile.ask_ai');
  const locale = getPreferredLocale();
  const { artworkData, aiMessages, setAiMessages } = useContext(ArtworkDataContext);
  const artworkId = artworkData?._id ?? null;
  const [question, setQuestion] = useState('');
  const [localMessages, setLocalMessages] = useState([]);
  const [welcomeMessage, setWelcomeMessage] = useState(EMERGENCY_WELCOME_MESSAGE);
  const [presetFollowups, setPresetFollowups] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const {
    clearQuotaMessage,
    handleAccessError,
    quotaMessage,
    setShowSuartePlusPopup,
    showSuartePlusPopup,
  } = useAskAiEntitlement();
  const conversationRef = useRef(null);
  const textareaRef = useRef(null);
  const hasSharedMessageState = Array.isArray(aiMessages) && typeof setAiMessages === 'function';
  const messages = hasSharedMessageState ? aiMessages : localMessages;
  const setMessages = hasSharedMessageState ? setAiMessages : setLocalMessages;
  const activeRevealMessageId = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (
        message?.role === 'assistant'
        && message?.isRevealing === true
        && typeof message?.fullContent === 'string'
      ) {
        return message.id;
      }
    }
    return null;
  }, [messages]);

  const artistDisplayName = useMemo(() => {
    const resolved = getArtworkArtistDisplayName(artworkData);
    return resolved && resolved !== '-' ? resolved : 'the artist';
  }, [artworkData]);
  const interpolationValues = useMemo(
    () => ({
      artistName: artistDisplayName,
      ArtistName: artistDisplayName,
    }),
    [artistDisplayName],
  );

  useEffect(() => {
    let active = true;

    const loadPreset = async () => {
      try {
        const { response, data } = await fetchWrapper.get(
          `${config.apis.api.url}/ai/artworks/preset`,
        );

        if (!response.ok) {
          if (active) {
            setWelcomeMessage(EMERGENCY_WELCOME_MESSAGE);
            setPresetFollowups([]);
          }
          return;
        }

        const preset = data?.preset ?? null;
        const nextWelcome = interpolateTemplate(
          preset?.default_message || '',
          interpolationValues,
        );
        const nextFollowups = Array.isArray(preset?.followups)
          ? preset.followups
            .map((followup) => interpolateTemplate(followup?.text, interpolationValues))
            .filter(Boolean)
          : [];

        if (!active) {
          return;
        }

        setWelcomeMessage(nextWelcome || EMERGENCY_WELCOME_MESSAGE);
        setPresetFollowups(nextFollowups);
      } catch (_error) {
        if (!active) {
          return;
        }
        setWelcomeMessage(EMERGENCY_WELCOME_MESSAGE);
        setPresetFollowups([]);
      }
    };

    void loadPreset();

    return () => {
      active = false;
    };
  }, [interpolationValues]);

  useEffect(() => {
    if (messages.length > 0) {
      return;
    }
    setMessages([
      {
        id: `ai-welcome-${artworkId ?? 'default'}`,
        role: 'assistant',
        content: welcomeMessage,
        timestamp: new Date(),
        hideMeta: true,
      },
    ]);
    setQuestion('');
    setError(null);
  }, [artworkId, messages.length, setMessages, welcomeMessage]);

  useEffect(() => {
    setMessages((prev) => {
      if (prev.length !== 1 || prev[0]?.id !== `ai-welcome-${artworkId ?? 'default'}` || prev[0]?.role !== 'assistant') {
        return prev;
      }

      if (prev[0]?.content === welcomeMessage) {
        return prev;
      }

      return [
        {
          ...prev[0],
          content: welcomeMessage,
        },
      ];
    });
  }, [artworkId, setMessages, welcomeMessage]);

  useEffect(() => {
    if (!conversationRef.current) return;
    conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
  }, [isSubmitting, messages]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    const lineHeight = Number.parseFloat(window.getComputedStyle(textarea).lineHeight) || 20;
    const maxHeight = lineHeight * 3;
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [question]);

  useEffect(() => {
    if (!activeRevealMessageId) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setMessages((prev) => {
        const messageIndex = prev.findIndex((message) => message.id === activeRevealMessageId);
        if (messageIndex < 0) {
          return prev;
        }

        const message = prev[messageIndex];
        if (!message?.isRevealing || typeof message?.fullContent !== 'string') {
          return prev;
        }

        const fullContent = message.fullContent;
        const currentLength = typeof message.content === 'string' ? message.content.length : 0;
        if (currentLength >= fullContent.length) {
          const finalizedMessages = [...prev];
          finalizedMessages[messageIndex] = {
            ...message,
            content: fullContent,
            isRevealing: false,
            fullContent: undefined,
          };
          return finalizedMessages;
        }

        const remaining = fullContent.length - currentLength;
        // Dynamic chunk size:
        // - Increase REVEAL_MIN_CHARS / REVEAL_MAX_CHARS to speed up.
        // - Decrease them to slow down.
        const chunkSize = Math.min(
          remaining,
          Math.max(REVEAL_MIN_CHARS, Math.min(REVEAL_MAX_CHARS, Math.ceil(remaining / 10))),
        );
        const nextContent = fullContent.slice(0, currentLength + chunkSize);
        const done = nextContent.length >= fullContent.length;
        const nextMessages = [...prev];
        nextMessages[messageIndex] = {
          ...message,
          content: nextContent,
          isRevealing: !done,
          fullContent: done ? undefined : fullContent,
        };
        return nextMessages;
      });
    }, REVEAL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [activeRevealMessageId, setMessages]);

  const submitQuestion = async (rawQuestion) => {
    if (isSubmitting || !artworkId) {
      return;
    }

    const trimmedQuestion = normalizeText(rawQuestion);
    if (!trimmedQuestion) {
      return;
    }

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmedQuestion,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setQuestion('');
    setError(null);
    clearQuotaMessage();
    setIsSubmitting(true);

    try {
      const { response, data } = await fetchWrapper.post(
        `${config.apis.api.url}/ai/artworks/${artworkId}/ask`,
        {
          optionalToken: true,
          includeCredentials: true,
          body: {
            question: trimmedQuestion,
          },
        },
      );

      if (!response.ok) {
        if (handleAccessError(data, t)) {
          return;
        }
        throw new Error(resolveAiAccessErrorMessage(data, t));
      }

      const answer = typeof data?.answer === 'string' ? data.answer.trim() : '';
      if (!answer) {
        throw new Error(t('errors.empty_answer'));
      }

      setMessages((prev) => [
        ...prev,
        {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: '',
          timestamp: new Date(),
          fullContent: answer,
          isRevealing: true,
          recommendedArtworks: Array.isArray(data?.recommended_artworks)
            ? data.recommended_artworks.filter(Boolean)
            : [],
        },
      ]);
    } catch (submitError) {
      const message =
        submitError instanceof Error && submitError.message
          ? submitError.message
          : t('errors.unable_to_answer');
      setError(message);
      setMessages((prev) => [
        ...prev,
        {
          id: `assistant-error-${Date.now()}`,
          role: 'assistant',
          content: t('messages.retry_later'),
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await submitQuestion(question);
  };

  const showInitialFollowups = presetFollowups.length > 0
    && messages.length === 1
    && messages[0]?.role === 'assistant'
    && !isSubmitting;

  return (
    <div
      className="artwork-view-ai__container"
      onTouchStart={(event) => event.stopPropagation()}
      onTouchEnd={(event) => event.stopPropagation()}
      onWheel={(event) => event.stopPropagation()}
    >
      <SocialsHeader close={closeSocial} type="ask-ai" />
      <div className="artwork-view-ai__main">
        <div className="artwork-view-ai__messages remove-scrollbar" ref={conversationRef}>
          {messages.map((message) => {
            const messageTime = message.hideMeta ? null : formatMessageTime(message.timestamp, locale);
            return (
              <div key={message.id} className={`artwork-view-ai__message artwork-view-ai__message--${message.role}${message.isRevealing ? ' artwork-view-ai__message--revealing' : ''}`}>
                <Text className="artwork-view-ai__message-text" paragraph>
                  {message.content}
                </Text>
                {message.role === 'assistant' && (message.recommendedArtworks?.length ?? 0) > 0 ? (
                  <div className="artwork-view-ai__recommendations">
                    {message.recommendedArtworks.map((artwork) =>
                      artwork?._id && artwork?.slug ? (
                        <AiRecommendArtworkCard
                          key={artwork._id}
                          artwork={artwork}
                          onClick={() => navigate(`/artwork/${artwork.slug}`, { state: { from: true } })}
                        />
                      ) : null,
                    )}
                  </div>
                ) : null}
                {messageTime ? <span className="artwork-view-ai__message-time">{messageTime}</span> : null}
              </div>
            );
          })}

          {isSubmitting ? (
            <div className="artwork-view-ai__typing" role="status" aria-live="polite">
              <span />
              <span />
              <span />
            </div>
          ) : null}
          <AskAiQuotaNotice
            message={quotaMessage}
            onSuartePlusClick={() => setShowSuartePlusPopup(true)}
          />
          {showInitialFollowups ? (
            <div className="artwork-view-ai__followups">
              {presetFollowups.map((followup) => (
                <button
                  key={followup}
                  type="button"
                  className="artwork-view-ai__followup"
                  onClick={() => void submitQuestion(followup)}
                  disabled={!artworkId || isSubmitting}
                >
                  {followup}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <form className="artwork-view-ai__form ai-conversation__composer" onSubmit={handleSubmit}>
          <textarea
            ref={textareaRef}
            className="artwork-view-ai__textarea ai-conversation__composer-textarea remove-scrollbar"
            placeholder="Ask about this artwork..."
            value={question}
            rows={1}
            maxLength={MAX_QUESTION_LENGTH}
            onChange={(event) => setQuestion(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                event.currentTarget.form?.requestSubmit();
              }
            }}
            disabled={!artworkId || isSubmitting}
          />
          <button
            type="submit"
            className="artwork-view-ai__submit ai-conversation__composer-submit"
            disabled={isSubmitting || !artworkId || !question.trim()}
            aria-label={t('actions.ask')}
            title={t('actions.ask')}
          >
            <SendIcon className="ai-conversation__composer-submit-icon" />
          </button>
        </form>

        {error ? (
          <Text className="artwork-view-ai__error" paragraph>
            {error}
          </Text>
        ) : null}
      </div>
      <SuartePlusPopup
        open={showSuartePlusPopup}
        onClose={() => setShowSuartePlusPopup(false)}
        highlightedBenefit="ask_ai"
      />
    </div>
  );
}
